import React from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Tag,
  InputNumber,
  message,
  Table,
  Divider,
  Tooltip,
} from "antd";
import { KPI_INDICATORS } from "../config/constants";

const { Text } = Typography;

const Step2 = ({
  clientId,
  getPairs,
  savedDefaults,
  selectedDefault,
  calcTotal,
  marketWeights,
  saveDefaultFor,
  applyDefaultTo,
  updateWeight,
  setSelectedDefault,
  setMarketWeights,
}) => {
  if (!clientId)
    return (
      <Card
        title="Market Weights"
        style={{ marginTop: 24 }}
      >
        <div style={{ textAlign: "center", padding: 40 }}>
          <Text type="secondary">Select client first</Text>
        </div>
      </Card>
    );

  const pairs = getPairs();

  const handleApplyToAll = () => {
    const def = savedDefaults.find((d) => d.id === selectedDefault);
    if (!def) return;
    setMarketWeights((p) => {
      const copy = { ...p };
      pairs.forEach(({ regionId, market }) => {
        copy[`${regionId}|${market}`] = {
          ...(def.weights || {}),
        };
      });
      return copy;
    });
    message.success("Applied to all");
  };

  const handleTagClick = (defId) => {
    setSelectedDefault(defId);
  };

  // Format template content for tooltip
  const formatTemplateContent = (weights) => {
    if (!weights || Object.keys(weights).length === 0) {
      return "No weights configured";
    }

    const total = Object.values(weights).reduce(
      (sum, weight) => sum + (parseFloat(weight) || 0),
      0
    );
    const isValid = Math.abs(total - 100) < 0.1;

    const weightList = KPI_INDICATORS.map((indicator) => {
      const weight = weights[indicator.key] || 0;
      return `• ${indicator.label}: ${weight}%`;
    }).join("\n");

    return `${weightList}\n\nTotal: ${total.toFixed(1)}% ${
      isValid ? "✓" : "⚠️"
    }`;
  };

  // Create table data
  const tableData = pairs.map(({ regionId, regionName, market }) => {
    const ck = `${regionId}|${market}`;
    const total = calcTotal(ck);
    const ok = Math.abs(total - 100) < 0.1;

    return {
      key: ck,
      regionId,
      regionName,
      market,
      total,
      ok,
      weights: marketWeights[ck] || {},
    };
  });

  const columns = [
    {
      title: "Region",
      dataIndex: "regionName",
      key: "region",
      width: 120,
      fixed: "left",
      render: (text) => (
        <Text
          strong
          style={{ fontSize: 12 }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: "Market",
      dataIndex: "market",
      key: "market",
      width: 80,
      fixed: "left",
      render: (text) => (
        <Text style={{ fontSize: 12 }}>{text.toUpperCase()}</Text>
      ),
    },
    ...KPI_INDICATORS.map((k) => ({
      title: k.label,
      key: k.key,
      width: 100,
      render: (_, record) => {
        const handleWeightChange = (v) =>
          updateWeight(record.key, k.key, v || 0);
        return (
          <InputNumber
            min={0}
            max={100}
            step={0.1}
            precision={1}
            size="small"
            style={{ width: 80 }}
            value={record.weights[k.key] || 0}
            onChange={handleWeightChange}
            addonAfter="%"
          />
        );
      },
    })),
    {
      title: "Total",
      key: "total",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Text
          strong
          style={{
            color: record.ok
              ? "#52c41a"
              : record.total > 100
              ? "#ff4d4f"
              : "#faad14",
            fontSize: 12,
          }}
        >
          {record.total.toFixed(1)}%
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size={4}>
          <Button
            size="small"
            onClick={() => saveDefaultFor(record.key)}
          >
            Save
          </Button>
          <Button
            size="small"
            disabled={!selectedDefault}
            onClick={() => applyDefaultTo(record.key)}
          >
            Apply
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Market Weight Configuration"
      style={{ marginTop: 24 }}
    >
      <Text
        type="secondary"
        style={{ display: "block", marginBottom: 12 }}
      >
        Set weights per KPI per market (must sum 100%). Use the table below for
        efficient bulk editing.
      </Text>

      {savedDefaults.length > 0 && (
        <>
          <Card
            type="inner"
            size="small"
            style={{ marginBottom: 16 }}
            title={<span style={{ fontSize: 13 }}>Weight Templates</span>}
            extra={
              <Button
                size="small"
                type="primary"
                disabled={!selectedDefault || pairs.length === 0}
                onClick={handleApplyToAll}
              >
                Apply To All Markets
              </Button>
            }
          >
            <Space
              size={8}
              wrap
            >
              {savedDefaults.map((def) => (
                <Tooltip
                  key={def.id}
                  title={
                    <div
                      style={{
                        whiteSpace: "pre-line",
                        fontSize: "12px",
                        maxWidth: "300px",
                        lineHeight: "1.4",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                          marginBottom: "8px",
                          borderBottom: "1px solid rgba(255,255,255,0.3)",
                          paddingBottom: "4px",
                          color: "#fff",
                        }}
                      >
                        📊 {def.name}
                      </div>
                      <div style={{ color: "#f0f0f0" }}>
                        {formatTemplateContent(def.weights)}
                      </div>
                    </div>
                  }
                  placement="top"
                  overlayStyle={{ maxWidth: "350px" }}
                >
                  <Tag
                    color={def.id === selectedDefault ? "blue" : "default"}
                    style={{ cursor: "pointer", padding: "4px 8px" }}
                    onClick={() => handleTagClick(def.id)}
                  >
                    {def.name}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </Card>
          <Divider style={{ margin: "12px 0" }} />
        </>
      )}

      {pairs.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
          Add regions & markets in Step 1.
        </div>
      ) : (
        <div style={{ maxHeight: 450, overflowY: "auto" }}>
          <Table
            dataSource={tableData}
            columns={columns}
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            rowClassName={(record) =>
              record.ok ? "" : record.total > 100 ? "row-error" : "row-warning"
            }
            style={{
              ".row-error": { backgroundColor: "#fff2f0" },
              ".row-warning": { backgroundColor: "#fffbe6" },
            }}
          />
        </div>
      )}

      <style jsx>{`
        .ant-table-tbody > tr.row-error > td {
          background-color: #fff2f0 !important;
        }
        .ant-table-tbody > tr.row-warning > td {
          background-color: #fffbe6 !important;
        }
      `}</style>
    </Card>
  );
};

export default Step2;
